// Clients, People and Artifacts, as tables.
//
// The questions these lists get asked are comparative — who owes us a reply, who
// has seen the most, where is the money sitting, what did we ship and when — so
// every fact gets a sortable column instead of being crammed into a muted subline.
//
// **Each kind gets its own columns.** This file used to branch on `person` and
// treat everything else as an org, which was true while there were only two of
// them; when artifact became its own record type it inherited the org's columns
// and a list of things we had made read People / Deals / Open / Won, four columns
// of dots. The switch is exhaustive now, so the next kind cannot land silently in
// someone else's table.

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ArchivedToggle, Avatar, Button, EmptyState, Pill } from '../components/common'
import { DataTable, Num, type Column } from '../components/DataTable'
import { Icon, iconName } from '../components/Icon'
import { LinkCell } from '../components/Links'
import { FilterInput, View } from '../components/Page'
import { useCreateEntity } from '../components/useCreate'
import { absoluteOf, formatDate, money, relativeShort } from '../lib/format'
import {
  ACCESS_LABELS,
  dueTone,
  isBooked,
  KINDS,
  labelOf,
  normalizeAccess,
  type EntityKind,
} from '../lib/model'
import {
  dealsForOrg,
  entitiesOfKind,
  fieldStr,
  internalOrgs,
  lastContact,
  lastTouch,
  linkedOfKind,
  milestonesForWorks,
  orgsForArtifact,
  projectsForOrg,
  titleOf,
  type EntityRecord,
} from '../lib/reducer'
import { useStore } from '../lib/store'

export function EntityListPage({ kind }: { kind: EntityKind }) {
  const { state, config, version } = useStore()
  const create = useCreateEntity()
  const navigate = useNavigate()
  const def = KINDS[kind]
  const [query, setQuery] = useState('')

  /** The people whose sign-in this deployment actually decides: those at the org
   *  marked Internal. Empty until one is, which keeps the column off a deployment
   *  that has not said who it is. */
  const homeTeam = useMemo(() => new Set(internalOrgs(state).flatMap((o) => o.links)), [state])
  const accessOf = (r: EntityRecord) => normalizeAccess(r.fields['access'])
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [showArchived, setShowArchived] = useState(false)

  const { rows, archivedCount, total } = useMemo(() => {
    void version
    const withArchived = entitiesOfKind(state, kind, true)
    const live = withArchived.filter((e) => !e.archived)
    const source = showArchived ? withArchived : live
    const q = query.trim().toLowerCase()
    return {
      total: live.length,
      archivedCount: withArchived.length - live.length,
      rows: source.filter(
        (r) =>
          !q ||
          titleOf(state, r.id).toLowerCase().includes(q) ||
          fieldStr(r, 'role').toLowerCase().includes(q) ||
          fieldStr(r, 'kind').toLowerCase().includes(q) ||
          fieldStr(r, 'location').toLowerCase().includes(q),
      ),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, kind, query, showArchived])

  const columns = useMemo<Column<EntityRecord>[]>(() => {
    const nameCol: Column<EntityRecord> = {
      key: 'name',
      label: 'Name',
      value: (r) => titleOf(state, r.id),
      render: (r) => (
        <span className="flex items-center gap-2">
          {kind === 'person' && (
            <Avatar actor={{ id: r.id, name: titleOf(state, r.id) || '?' }} size={20} />
          )}
          <span className="truncate font-medium">{titleOf(state, r.id) || 'Untitled'}</span>
          {r.archived && <span className="badge-archived">Archived</span>}
        </span>
      ),
    }
    const touched: Column<EntityRecord> = {
      key: 'touched',
      label: 'Touched',
      align: 'right',
      width: 96,
      hideBelow: 448,
      value: (r) => lastTouch(state, r.id),
      render: (r) => (
        <span
          className="num"
          style={{ fontSize: 'var(--text-label)', color: 'var(--faint)' }}
          title={absoluteOf(lastTouch(state, r.id))}
        >
          {relativeShort(lastTouch(state, r.id))}
        </span>
      ),
    }

    if (kind === 'person') {
      return [
        nameCol,
        {
          key: 'role',
          label: 'Role',
          width: 180,
          value: (r) => fieldStr(r, 'role') || 'zz',
          render: (r) => (
            <span style={{ color: 'var(--muted)' }}>{fieldStr(r, 'role') || '·'}</span>
          ),
        },
        {
          key: 'orgs',
          label: 'Orgs',
          width: 220,
          hideBelow: 672,
          value: (r) =>
            linkedOfKind(state, r.id, 'org')
              .map((o) => fieldStr(o, 'name'))
              .join(', ') || 'zz',
          render: (r) => {
            const orgs = linkedOfKind(state, r.id, 'org')
            return orgs.length ? (
              <span className="truncate" style={{ color: 'var(--muted)' }}>
                {orgs.map((o) => fieldStr(o, 'name')).join(', ')}
              </span>
            ) : (
              <span style={{ color: 'var(--faint)' }}>·</span>
            )
          },
        },
        {
          key: 'email',
          label: 'Email',
          width: 200,
          hideBelow: 1024,
          sortable: false,
          render: (r) => {
            const email = fieldStr(r, 'email')
            return email ? (
              <a
                href={`mailto:${email}`}
                onClick={(e) => e.stopPropagation()}
                className="hover:underline"
                style={{
                  color: 'var(--marine)',
                  fontFamily: 'var(--mono)',
                  fontSize: 'var(--text-label)',
                }}
              >
                {email}
              </a>
            ) : (
              <span style={{ color: 'var(--faint)' }}>·</span>
            )
          },
        },
        // Only where it means something. On a deployment with no home org set, and
        // for every client contact on one that has, this column would say "Team
        // member" about people who cannot sign in and never could — an access
        // control that lies in the reassuring direction.
        ...(homeTeam.size > 0
          ? [
              {
                key: 'access',
                label: 'Sign-in',
                width: 150,
                hideBelow: 896,
                value: (r: EntityRecord) => (homeTeam.has(r.id) ? accessOf(r) : 'zz'),
                render: (r: EntityRecord) => {
                  if (!homeTeam.has(r.id)) return <span style={{ color: 'var(--faint)' }}>·</span>
                  if (!fieldStr(r, 'email')) {
                    return <span style={{ color: 'var(--faint)' }}>no email</span>
                  }
                  const def = labelOf(ACCESS_LABELS, accessOf(r))
                  return def ? <Pill def={def} label={def.label} /> : null
                },
              } satisfies Column<EntityRecord>,
            ]
          : []),
        touched,
      ]
    }

    if (kind === 'artifact') {
      const orgsOf = (r: EntityRecord) => orgsForArtifact(state, r.id)
      const fromOf = (r: EntityRecord) => linkedOfKind(state, r.id, 'project')
      return [
        nameCol,
        {
          key: 'kind',
          label: 'Kind',
          width: 116,
          // 'zz' sorts the unset ones last rather than first, which is where a
          // record nobody has classified belongs.
          value: (r) => labelOf(config.artifactKinds, fieldStr(r, 'kind'))?.label ?? 'zz',
          render: (r) => {
            const k = labelOf(config.artifactKinds, fieldStr(r, 'kind'))
            return k ? (
              <Pill def={k} label={k.label} />
            ) : (
              <span style={{ color: 'var(--faint)' }}>·</span>
            )
          },
        },
        {
          key: 'client',
          label: 'Client',
          width: 200,
          value: (r) =>
            orgsOf(r)
              .map((o) => fieldStr(o, 'name'))
              .join(', ') || 'zz',
          render: (r) => {
            const orgs = orgsOf(r)
            return orgs.length ? (
              <span className="truncate" style={{ color: 'var(--muted)' }}>
                {orgs.map((o) => fieldStr(o, 'name')).join(', ')}
              </span>
            ) : (
              <span style={{ color: 'var(--faint)' }}>·</span>
            )
          },
        },
        {
          key: 'from',
          label: 'Came out of',
          width: 200,
          hideBelow: 896,
          value: (r) =>
            fromOf(r)
              .map((w) => titleOf(state, w.id))
              .join(', ') || 'zz',
          render: (r) => {
            const from = fromOf(r)
            return from.length ? (
              <span className="truncate" style={{ color: 'var(--muted)' }}>
                {from.map((w) => titleOf(state, w.id)).join(', ')}
              </span>
            ) : (
              <span style={{ color: 'var(--faint)' }}>·</span>
            )
          },
        },
        {
          key: 'link',
          label: 'Link',
          width: 200,
          hideBelow: 672,
          value: (r) => fieldStr(r, 'links') || 'zz',
          render: (r) => <LinkCell value={fieldStr(r, 'links') || fieldStr(r, 'url')} />,
        },
        {
          key: 'delivered',
          label: 'Delivered',
          align: 'right',
          width: 116,
          // The artifact's own date, which is the one worth sorting by. `Touched`
          // says when someone last edited the record, and on 29 records imported
          // in one migration it said "today" 29 times.
          value: (r) => fieldStr(r, 'delivered') || '',
          render: (r) => {
            const on = fieldStr(r, 'delivered')
            return on ? (
              <span
                className="num"
                style={{ fontSize: 'var(--text-label)', color: 'var(--muted)' }}
              >
                {formatDate(on)}
              </span>
            ) : (
              <span style={{ color: 'var(--faint)' }}>·</span>
            )
          },
        },
      ]
    }

    const openOf = (r: EntityRecord) =>
      dealsForOrg(state, r.id)
        .filter((d) => config.stages.find((s) => s.key === fieldStr(d, 'stage'))?.kind === 'open')
        .reduce((sum, d) => sum + (Number(d.fields.value) || 0), 0)
    const nextOf = (r: EntityRecord) =>
      milestonesForWorks(
        state,
        projectsForOrg(state, r.id).map((w) => w.id),
      )[0]
    // Everything ever booked on this client, not the deals sitting in the stage
    // called Won: a finished engagement leaves that stage, and a column that read
    // `·` against a client we delivered six figures for was answering a question
    // nobody asks.
    const bookedOf = (r: EntityRecord) =>
      dealsForOrg(state, r.id)
        .filter((d) => isBooked(config.stages.find((s) => s.key === fieldStr(d, 'stage'))?.kind))
        .reduce((sum, d) => sum + (Number(d.fields.value) || 0), 0)

    return [
      nameCol,
      {
        key: 'people',
        label: 'People',
        align: 'right',
        width: 68,
        hideBelow: 672,
        value: (r) => linkedOfKind(state, r.id, 'person').length,
        render: (r) => <Num value={linkedOfKind(state, r.id, 'person').length} />,
      },
      {
        key: 'deals',
        label: 'Deals',
        align: 'right',
        width: 62,
        hideBelow: 448,
        value: (r) => dealsForOrg(state, r.id).length,
        render: (r) => <Num value={dealsForOrg(state, r.id).length} />,
      },
      {
        key: 'open',
        label: 'Open',
        align: 'right',
        width: 92,
        value: (r) => openOf(r),
        render: (r) => <Num value={money(openOf(r), '·')} hue="--ember" />,
      },
      {
        key: 'booked',
        label: 'Booked',
        align: 'right',
        width: 92,
        value: (r) => bookedOf(r),
        render: (r) => <Num value={money(bookedOf(r), '·')} hue="--good" />,
      },
      {
        // Not `Touched`. Touched counts edits, so a migration that imported forty
        // clients in one go reported "today" forty times and told you nothing. This
        // counts CONTACT, and it looks at the client's deals and projects too,
        // because a call is usually filed on the narrower record it was about.
        key: 'spoke',
        label: 'Last spoke',
        align: 'right',
        width: 104,
        hideBelow: 448,
        value: (r) => lastContact(state, r.id) || '0',
        render: (r) => {
          const when = lastContact(state, r.id)
          return when ? (
            <span
              className="num"
              style={{ fontSize: 'var(--text-label)', color: 'var(--muted)' }}
              title={absoluteOf(when)}
            >
              {relativeShort(when)}
            </span>
          ) : (
            <span style={{ color: 'var(--faint)' }}>never</span>
          )
        },
      },
      {
        key: 'next',
        label: 'Next',
        width: 190,
        hideBelow: 896,
        value: (r) => nextOf(r)?.due ?? 'zz',
        render: (r) => {
          const m = nextOf(r)
          return m ? (
            <span className="truncate" style={{ fontSize: 'var(--text-meta)' }}>
              <span className="num" style={{ color: dueTone(m.due), marginRight: 6 }}>
                {m.due}
              </span>
              <span style={{ color: 'var(--muted)' }}>{m.label}</span>
            </span>
          ) : (
            <span style={{ color: 'var(--faint)' }}>·</span>
          )
        },
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, kind, config])

  return (
    <View
      actions={
        <>
          <FilterInput value={query} onChange={setQuery} />
          <ArchivedToggle
            count={archivedCount}
            on={showArchived}
            onToggle={() => setShowArchived((v) => !v)}
          />
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Icon name="Plus" size={14} /> New
          </Button>
        </>
      }
    >
      {creating && (
        <div
          className="rounded-control mb-4 flex items-center gap-2 border p-2"
          style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
        >
          <Icon name={iconName(def.icon)} size={16} style={{ color: 'var(--accent)' }} />
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim()) {
                void create(kind, name)
                setName('')
                setCreating(false)
              }
              if (e.key === 'Escape') setCreating(false)
            }}
            onBlur={() => setCreating(false)}
            placeholder={def.fields.find((f) => f.title)?.placeholder ?? `New ${def.singular}`}
            className="text-body flex-1 bg-transparent outline-none"
          />
          <span className="meta">Enter to create</span>
        </div>
      )}

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(r) => r.id}
        onOpen={(r) => navigate(r.kind === 'org' ? `/c/${r.id}` : `/e/${r.id}`)}
        onOpenFull={(r) => navigate(`/e/${r.id}`)}
        openLabel={(r) => titleOf(state, r.id)}
        // Nothing is hatched: an org has no dormant state to hatch for, and a
        // project's is read off its deals rather than stored.
        rowClass={() => ''}
        storageKey={`crum.${kind}.sort`}
        initialSort={
          kind === 'org'
            ? { key: 'spoke', dir: 'desc' }
            : kind === 'artifact'
              ? { key: 'delivered', dir: 'desc' }
              : { key: 'name', dir: 'asc' }
        }
        empty={
          <EmptyState
            icon={iconName(def.icon)}
            title={total === 0 ? `No ${def.plural.toLowerCase()} yet` : 'No matches'}
            hint={
              total === 0
                ? kind === 'artifact'
                  ? 'A delivered thing: a demo, a document, a dataset, a service you run.'
                  : 'Deals, projects and notes can all point at one.'
                : undefined
            }
            action={
              total === 0 ? (
                <Button variant="primary" onClick={() => setCreating(true)}>
                  <Icon name="Plus" size={14} /> New {def.singular.toLowerCase()}
                </Button>
              ) : undefined
            }
          />
        }
      />
    </View>
  )
}
